import { useState, useRef, useCallback } from "react";
import { MessageCircle, Volume2, VolumeX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/utils/tracking";
import { openWhatsApp as openWhatsAppHelper } from "@/utils/phone";

const VideoSection = ({ content }) => {
  const videoContent = content?.video || {};
  const isActive = videoContent.active !== undefined ? videoContent.active : true;
  const videoUrl = videoContent.url || "/images/projects/bekaj-bau-reel.mp4";
  const headline = videoContent.headline || "Schreib mir jetzt eine WhatsApp";
  const subheadline = videoContent.subheadline || "";
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef(null);

  const isLocalVideo = videoUrl.endsWith('.mp4') || videoUrl.startsWith('/images');
  const isYouTube = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be');
  const isVimeo = videoUrl.includes('vimeo.com');

  const getEmbedUrl = (url) => {
    const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\s]+)/);
    if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}?autoplay=1&mute=1&loop=1`;
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&muted=1&loop=1`;
    return null;
  };

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  }, []);

  if (!isActive) return null;

  const openWhatsApp = () => {
    const whatsappNumber = '4917620283482';
    const message = "Hallo BEKAJ BAU,\nich habe Ihr Video gesehen und interessiere mich für eine Bauleistung.";
    openWhatsAppHelper(whatsappNumber, message);
    trackEvent('contact_whatsapp', { source: 'video_section' });
  };

  return (
    <section className="py-16 sm:py-20 bg-[#f5f0ea]" data-testid="video-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-10">
          <h2
            className="text-3xl sm:text-4xl font-bold text-[#4A331D] mb-3"
            style={{ fontFamily: "Work Sans, sans-serif" }}
          >
            {headline}
          </h2>
          {subheadline && (
            <p className="text-base sm:text-lg text-gray-600">{subheadline}</p>
          )}
        </div>

        <div className="flex flex-col items-center gap-6">
          {/* Smartphone / Reel Frame */}
          <div
            className="relative w-full max-w-[320px] sm:max-w-[380px] lg:max-w-[420px] mx-auto"
            data-testid="video-reel-container"
          >
            {/* Phone frame */}
            <div className="bg-[#1a1a1a] rounded-[2rem] p-2 shadow-2xl border border-[#333]">
              {/* Notch */}
              <div className="absolute top-4 left-1/2 -translate-x-1/2 w-24 h-5 bg-[#1a1a1a] rounded-b-xl z-10" />

              {/* Video content */}
              <div className="rounded-[1.5rem] overflow-hidden bg-black aspect-[9/16] relative">
                {isLocalVideo ? (
                  <>
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      autoPlay
                      muted
                      loop
                      playsInline
                      loading="lazy"
                      className="w-full h-full object-cover"
                      data-testid="video-reel-player"
                    />
                    <button
                      onClick={toggleMute}
                      className="absolute bottom-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors z-10"
                      aria-label={isMuted ? "Ton aktivieren" : "Ton deaktivieren"}
                      data-testid="video-mute-toggle"
                    >
                      {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                  </>
                ) : (isYouTube || isVimeo) ? (
                  <iframe
                    src={getEmbedUrl(videoUrl)}
                    title="BEKAJ BAU Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/50 text-sm">
                    Video wird geladen...
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* WhatsApp CTA under video */}
          <Button
            onClick={openWhatsApp}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white px-8 py-5 text-base font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all"
            data-testid="video-whatsapp-cta"
          >
            <MessageCircle className="w-5 h-5 mr-2" />
            Jetzt per WhatsApp schreiben
          </Button>
        </div>
      </div>
    </section>
  );
};

export default VideoSection;
